import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import StockMagasinPicker from '../features/stock/StockMagasinPicker';
import { STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, STOCK_BTN_ICON } from '../features/stock/stockV2Ui';
import { STOCK_TABS } from './_stockTabs';
import {
  Truck, Plus, RefreshCw, X, Package, PackageCheck, Ban, Link2, Link2Off,
  ChevronDown, ChevronRight, Trash2, Wrench,
} from 'lucide-react';

// CSS classes par statut (inchangé). Les clés (en_attente, …) sont des valeurs backend : on ne les traduit pas.
const STATUT_META = {
  en_attente: { cls: 'bg-amber-950/40 text-amber-300 border-amber-500/30' },
  partielle: { cls: 'bg-sky-950/40 text-sky-300 border-sky-500/30' },
  recue: { cls: 'bg-emerald-950/40 text-emerald-300 border-emerald-500/30' },
  annulee: { cls: 'bg-gray-800/60 text-gray-500 border-white/10' },
};

// Mapping valeur backend -> clé i18n (affichage uniquement).
const STATUT_LABEL_KEYS = {
  en_attente: 'partsOrders.statusPending',
  partielle: 'partsOrders.statusPartial',
  recue: 'partsOrders.statusReceived',
  annulee: 'partsOrders.statusCancelled',
};

const STATUT_FILTERS = [
  { key: '', labelKey: 'partsOrders.filterAll' },
  { key: 'en_attente', labelKey: 'partsOrders.statusPending' },
  { key: 'partielle', labelKey: 'partsOrders.statusPartial' },
  { key: 'recue', labelKey: 'partsOrders.filterReceived' },
  { key: 'annulee', labelKey: 'partsOrders.filterCancelled' },
];

const PART_TYPES = [
  { value: 'standard', labelKey: 'partsOrders.partTypeStandard' },
  { value: 'piece_detachee', labelKey: 'partsOrders.partTypeSpare' },
  { value: 'serialise', labelKey: 'partsOrders.partTypeSerialized' },
];

const emptyLine = () => ({
  designation: '', reference: '', categorie: '', part_type: 'standard',
  suivi_imei: false, qty: 1, prix_achat_unitaire: '', prix_vente_prevu: '', main_oeuvre: '', location: 'boutique',
});

const inputCls = 'w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm min-h-[40px] focus:border-red-500/50 outline-none';

const StatutBadge = ({ statut }) => {
  const { t } = useLanguage();
  const m = STATUT_META[statut] || STATUT_META.en_attente;
  const labelKey = STATUT_LABEL_KEYS[statut] || STATUT_LABEL_KEYS.en_attente;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${m.cls}`}>
      {t(labelKey)}
    </span>
  );
};

// ── Modale création commande ──────────────────────────────────────────────
const CreateModal = ({ magasinsList, defaultMagasin, onClose, onCreated, api }) => {
  const { t } = useLanguage();
  const [magasinId, setMagasinId] = useState(defaultMagasin || 'M1');
  const [fournisseur, setFournisseur] = useState('');
  const [notes, setNotes] = useState('');
  const [lignes, setLignes] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setLigne = (idx, patch) => setLignes((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLigne = () => setLignes((prev) => [...prev, emptyLine()]);
  const removeLigne = (idx) => setLignes((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const submit = async () => {
    setError('');
    const clean = lignes
      .filter((l) => (l.designation || '').trim())
      .map((l) => ({
        designation: l.designation.trim(),
        reference: (l.reference || '').trim(),
        categorie: (l.categorie || '').trim(),
        part_type: l.part_type,
        suivi_imei: l.part_type === 'serialise' || !!l.suivi_imei,
        qty: Math.max(1, parseInt(l.qty, 10) || 1),
        prix_achat_unitaire: parseFloat(l.prix_achat_unitaire) || 0,
        prix_vente_prevu: parseFloat(l.prix_vente_prevu) || 0,
        main_oeuvre: parseFloat(l.main_oeuvre) || 0,
        location: l.location || 'boutique',
      }));
    if (!clean.length) { setError(t('partsOrders.errorNeedLine')); return; }
    setSaving(true);
    try {
      const r = await api.post('/commandes-pieces', { magasin_id: magasinId, fournisseur, notes, lignes: clean });
      onCreated(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('partsOrders.errorCreate'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-3 overflow-y-auto" data-testid="cp-create-modal">
      <div className={`${STOCK_PANEL} w-full max-w-3xl my-6 p-5`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Truck className="w-5 h-5 text-red-400" /> {t('partsOrders.createTitle')}</h3>
          <button type="button" onClick={onClose} className={STOCK_BTN_ICON}><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <StockMagasinPicker magasinsList={magasinsList} value={magasinId} onChange={setMagasinId} compact />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{t('partsOrders.supplier')}</p>
            <input className={inputCls} value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} placeholder={t('partsOrders.supplierPlaceholder')} data-testid="cp-fournisseur" />
          </div>
        </div>

        <div className="space-y-3 mb-3">
          {lignes.map((l, idx) => (
            <div key={idx} className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2" data-testid={`cp-line-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-500">{t('partsOrders.line', { num: idx + 1 })}</span>
                {lignes.length > 1 && (
                  <button type="button" onClick={() => removeLigne(idx)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input className={inputCls} value={l.designation} onChange={(e) => setLigne(idx, { designation: e.target.value })} placeholder={t('partsOrders.designationPlaceholder')} data-testid={`cp-line-desig-${idx}`} />
                <input className={inputCls} value={l.reference} onChange={(e) => setLigne(idx, { reference: e.target.value })} placeholder={t('partsOrders.referencePlaceholder')} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.type')}</label>
                  <select className={inputCls} value={l.part_type} onChange={(e) => setLigne(idx, { part_type: e.target.value })}>
                    {PART_TYPES.map((p) => <option key={p.value} value={p.value}>{t(p.labelKey)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.qty')}</label>
                  <input type="number" min="1" className={inputCls} value={l.qty} onChange={(e) => setLigne(idx, { qty: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.purchasePriceUnit')}</label>
                  <input type="number" step="0.01" className={inputCls} value={l.prix_achat_unitaire} onChange={(e) => setLigne(idx, { prix_achat_unitaire: e.target.value })} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.expectedSalePrice')}</label>
                  <input type="number" step="0.01" className={inputCls} value={l.prix_vente_prevu} onChange={(e) => setLigne(idx, { prix_vente_prevu: e.target.value })} placeholder="0.00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.category')}</label>
                  <input className={inputCls} value={l.categorie} onChange={(e) => setLigne(idx, { categorie: e.target.value })} placeholder={t('partsOrders.categoryPlaceholder')} />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.location')}</label>
                  <select className={inputCls} value={l.location} onChange={(e) => setLigne(idx, { location: e.target.value })}>
                    <option value="boutique">{t('partsOrders.locationShop')}</option>
                    <option value="depot">{t('partsOrders.locationWarehouse')}</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button type="button" onClick={addLigne} className={`${STOCK_BTN_SECONDARY} mb-4`} data-testid="cp-add-line">
          <Plus className="w-4 h-4" /> {t('partsOrders.addLine')}
        </button>

        <div className="mb-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{t('partsOrders.notes')}</p>
          <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {error && <div className="mb-3 text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={STOCK_BTN_SECONDARY}>{t('partsOrders.cancel')}</button>
          <button type="button" onClick={submit} disabled={saving} className={STOCK_BTN_PRIMARY} data-testid="cp-create-submit">
            {saving ? t('partsOrders.creating') : t('partsOrders.create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modale réception ──────────────────────────────────────────────────────
const ReceiveModal = ({ commande, onClose, onReceived, api }) => {
  const { t } = useLanguage();
  const openLines = (commande.lignes || []).filter((l) => (l.qty - (l.received_qty || 0)) > 0);
  const [rows, setRows] = useState(() => openLines.map((l) => {
    const remaining = l.qty - (l.received_qty || 0);
    return {
      ligne_id: l.id, designation: l.designation, remaining,
      suivi_imei: l.suivi_imei || l.part_type === 'serialise',
      received_qty: remaining, location: l.location || 'boutique', serialsText: '',
    };
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setRow = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const submit = async () => {
    setError('');
    const receptions = rows
      .filter((r) => (parseInt(r.received_qty, 10) || 0) > 0)
      .map((r) => ({
        ligne_id: r.ligne_id,
        received_qty: Math.min(parseInt(r.received_qty, 10) || 0, r.remaining),
        location: r.location,
        serials: r.serialsText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean),
      }));
    if (!receptions.length) { setError(t('partsOrders.errorNeedQty')); return; }
    setSaving(true);
    try {
      const r = await api.post(`/commandes-pieces/${commande.id}/receive`, { receptions });
      onReceived(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('partsOrders.errorReceive'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-3 overflow-y-auto" data-testid="cp-receive-modal">
      <div className={`${STOCK_PANEL} w-full max-w-2xl my-6 p-5`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><PackageCheck className="w-5 h-5 text-emerald-400" /> {t('partsOrders.receiveTitle', { ref: commande.ref_commande })}</h3>
          <button type="button" onClick={onClose} className={STOCK_BTN_ICON}><X className="w-4 h-4" /></button>
        </div>

        {rows.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">{t('partsOrders.allReceived')}</p>
        ) : (
          <div className="space-y-3">
            {rows.map((r, idx) => (
              <div key={r.ligne_id} className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white truncate">{r.designation}</span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{t('partsOrders.remaining', { count: r.remaining })}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.receivedQty')}</label>
                    <input type="number" min="0" max={r.remaining} className={inputCls} value={r.received_qty} onChange={(e) => setRow(idx, { received_qty: e.target.value })} data-testid={`cp-recv-qty-${idx}`} />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.location')}</label>
                    <select className={inputCls} value={r.location} onChange={(e) => setRow(idx, { location: e.target.value })}>
                      <option value="boutique">{t('partsOrders.locationShop')}</option>
                      <option value="depot">{t('partsOrders.locationWarehouse')}</option>
                    </select>
                  </div>
                </div>
                {r.suivi_imei && (
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">{t('partsOrders.serialsLabel')}</label>
                    <textarea className={inputCls} rows={2} value={r.serialsText} onChange={(e) => setRow(idx, { serialsText: e.target.value })} placeholder="123456789012345" data-testid={`cp-recv-serials-${idx}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {error && <div className="mt-3 text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className={STOCK_BTN_SECONDARY}>{t('partsOrders.close')}</button>
          {rows.length > 0 && (
            <button type="button" onClick={submit} disabled={saving} className={STOCK_BTN_PRIMARY} data-testid="cp-receive-submit">
              {saving ? t('partsOrders.validating') : t('partsOrders.validateReceive')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Modale liaison réparation ───────────────────────────────────────────────
const LinkRepairModal = ({ commande, ligne, onClose, onLinked, api }) => {
  const { t } = useLanguage();
  const [ref, setRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!ref.trim()) { setError(t('partsOrders.errorNeedRef')); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post(`/commandes-pieces/${commande.id}/lignes/${ligne.id}/link-repair`, { device_ref: ref.trim() });
      onLinked(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('partsOrders.errorRepairNotFound'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3" data-testid="cp-link-modal">
      <div className={`${STOCK_PANEL} w-full max-w-md p-5`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white flex items-center gap-2"><Wrench className="w-5 h-5 text-sky-400" /> {t('partsOrders.linkTitle')}</h3>
          <button type="button" onClick={onClose} className={STOCK_BTN_ICON}><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">{t('partsOrders.linkDescription', { designation: ligne.designation })}</p>
        <input className={inputCls} value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t('partsOrders.linkRefPlaceholder')} data-testid="cp-link-ref" />
        {error && <div className="mt-3 text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className={STOCK_BTN_SECONDARY}>{t('partsOrders.cancel')}</button>
          <button type="button" onClick={submit} disabled={saving} className={STOCK_BTN_PRIMARY} data-testid="cp-link-submit">
            {saving ? '…' : t('partsOrders.assign')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Carte commande ──────────────────────────────────────────────────────────
const CommandeCard = ({ commande, onChanged, onReceive, api }) => {
  const { t, formatCurrency, formatDate } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkLigne, setLinkLigne] = useState(null);

  const totalQty = (commande.lignes || []).reduce((s, l) => s + (l.qty || 0), 0);
  const recvQty = (commande.lignes || []).reduce((s, l) => s + (l.received_qty || 0), 0);
  const totalAchat = (commande.lignes || []).reduce((s, l) => s + (l.prix_achat_unitaire || 0) * (l.qty || 0), 0);

  const dateStr = commande.date_commande
    ? formatDate(commande.date_commande, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const cancel = async () => {
    if (!window.confirm(t('partsOrders.confirmCancel'))) return;
    setBusy(true);
    try {
      const r = await api.post(`/commandes-pieces/${commande.id}/cancel`);
      onChanged(r.data);
    } catch (e) { window.alert(e.response?.data?.detail || t('partsOrders.errorCancel')); }
    finally { setBusy(false); }
  };

  const unlink = async (ligne) => {
    setBusy(true);
    try {
      const r = await api.post(`/commandes-pieces/${commande.id}/lignes/${ligne.id}/unlink-repair`);
      onChanged(r.data);
    } catch (e) { window.alert(e.response?.data?.detail || t('partsOrders.errorGeneric')); }
    finally { setBusy(false); }
  };

  const closed = commande.statut === 'recue' || commande.statut === 'annulee';

  return (
    <div className={`${STOCK_PANEL} p-4`} data-testid={`cp-card-${commande.id}`}>
      <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <div className="flex items-start gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-gray-500 mt-1 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 mt-1 shrink-0" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white">{commande.ref_commande}</span>
              <StatutBadge statut={commande.statut} />
              <span className="text-xs text-gray-500 px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{commande.magasin_id}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1 truncate">
              {commande.fournisseur || t('partsOrders.supplierUnknown')} · {t('partsOrders.linesCount', { count: commande.lignes?.length || 0 })} · {t('partsOrders.receivedCount', { received: recvQty, total: totalQty })} · {dateStr}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-white">{formatCurrency(Number(totalAchat) || 0)}</div>
          <div className="text-[10px] text-gray-500">{t('partsOrders.purchaseCost')}</div>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] text-gray-500 uppercase border-b border-white/10">
                  <th className="px-2 py-2 text-left">{t('partsOrders.colDesignation')}</th>
                  <th className="px-2 py-2 text-center">{t('partsOrders.type')}</th>
                  <th className="px-2 py-2 text-center">{t('partsOrders.colReceivedQty')}</th>
                  <th className="px-2 py-2 text-right">{t('partsOrders.colPurchaseUnit')}</th>
                  <th className="px-2 py-2 text-right">{t('partsOrders.colSalePlanned')}</th>
                  <th className="px-2 py-2 text-left">{t('partsOrders.colRepair')}</th>
                  <th className="px-2 py-2 text-right">{t('partsOrders.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {(commande.lignes || []).map((l) => {
                  const received = (l.received_qty || 0) >= (l.qty || 0);
                  return (
                    <tr key={l.id} className="border-b border-white/5">
                      <td className="px-2 py-2 text-gray-200">
                        {l.designation}
                        {l.reference && <span className="text-gray-600 text-xs ml-1">({l.reference})</span>}
                      </td>
                      <td className="px-2 py-2 text-center text-xs text-gray-400">
                        {l.suivi_imei || l.part_type === 'serialise' ? t('partsOrders.typeSerialized') : (l.part_type === 'piece_detachee' ? t('partsOrders.typeSpareShort') : t('partsOrders.typeStandardShort'))}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={received ? 'text-emerald-400 font-semibold' : 'text-amber-300'}>{l.received_qty || 0}/{l.qty}</span>
                      </td>
                      <td className="px-2 py-2 text-right text-gray-400">{formatCurrency(Number(l.prix_achat_unitaire) || 0)}</td>
                      <td className="px-2 py-2 text-right text-gray-300">{formatCurrency(Number(l.prix_vente_prevu) || 0)}</td>
                      <td className="px-2 py-2 text-left">
                        {l.device_ref ? (
                          <span className="inline-flex items-center gap-1 text-xs text-sky-300">
                            <Wrench className="w-3 h-3" /> {l.device_ref}
                          </span>
                        ) : <span className="text-gray-600 text-xs">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {!closed && !received && (
                          l.device_ref ? (
                            <button type="button" onClick={() => unlink(l)} disabled={busy} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-red-400" title={t('partsOrders.detach')}>
                              <Link2Off className="w-4 h-4" />
                            </button>
                          ) : (
                            <button type="button" onClick={() => setLinkLigne(l)} disabled={busy} className="p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-sky-400" title={t('partsOrders.linkTitle')} data-testid={`cp-link-btn-${l.id}`}>
                              <Link2 className="w-4 h-4" />
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {commande.notes && <p className="text-xs text-gray-500 italic">{commande.notes}</p>}

          {!closed && (
            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-white/10">
              <button type="button" onClick={cancel} disabled={busy} className={STOCK_BTN_SECONDARY} data-testid={`cp-cancel-${commande.id}`}>
                <Ban className="w-4 h-4" /> {t('partsOrders.cancel')}
              </button>
              <button type="button" onClick={() => onReceive(commande)} disabled={busy} className={STOCK_BTN_PRIMARY} data-testid={`cp-receive-${commande.id}`}>
                <PackageCheck className="w-4 h-4" /> {t('partsOrders.receive')}
              </button>
            </div>
          )}
        </div>
      )}

      {linkLigne && (
        <LinkRepairModal
          commande={commande}
          ligne={linkLigne}
          api={api}
          onClose={() => setLinkLigne(null)}
          onLinked={(updated) => { setLinkLigne(null); onChanged(updated); }}
        />
      )}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
const CommandesPiecesPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [magasinsList, setMagasinsList] = useState([]);
  const [magFilter, setMagFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [receiving, setReceiving] = useState(null);

  useEffect(() => {
    api.get('/magasins?active_only=true')
      .then((r) => setMagasinsList(r.data?.magasins || []))
      .catch(() => setMagasinsList([]));
  }, [api]);

  const fetchCommandes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (magFilter) params.set('magasin_id', magFilter);
      if (statutFilter) params.set('statut', statutFilter);
      const r = await api.get(`/commandes-pieces?${params.toString()}`);
      setCommandes(r.data || []);
    } catch { setCommandes([]); }
    setLoading(false);
  }, [api, magFilter, statutFilter]);

  useEffect(() => { fetchCommandes(); }, [fetchCommandes]);

  const applyUpdated = (updated) => {
    setCommandes((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  const kpis = useMemo(() => {
    const enCours = commandes.filter((c) => c.statut === 'en_attente' || c.statut === 'partielle').length;
    const lignesEnCommande = commandes.reduce((s, c) => s + (c.lignes || []).filter((l) => l.device_ref && (l.received_qty || 0) < (l.qty || 0)).length, 0);
    const coutOuvert = commandes
      .filter((c) => c.statut !== 'annulee' && c.statut !== 'recue')
      .reduce((s, c) => s + (c.lignes || []).reduce((ss, l) => ss + (l.prix_achat_unitaire || 0) * ((l.qty || 0) - (l.received_qty || 0)), 0), 0);
    return { enCours, lignesEnCommande, coutOuvert };
  }, [commandes]);

  return (
    <Layout api={api} user={user}>
      <div className="max-w-6xl mx-auto space-y-4 fade-in" data-testid="commandes-pieces-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader title={t('partsOrders.pageTitle')} subtitle={t('partsOrders.pageSubtitle')} backTestId="cp-back" />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-xl border border-white/10 overflow-hidden bg-black/20">
              <button type="button" onClick={() => setMagFilter('')}
                className={`px-3 py-2 min-h-[40px] text-xs font-semibold transition-colors ${magFilter === '' ? 'bg-red-600 text-white' : 'text-gray-500 hover:bg-white/5'}`}>{t('partsOrders.filterAllStores')}</button>
              {magasinsList.map((m) => (
                <button key={m.code} type="button" onClick={() => setMagFilter(m.code)}
                  className={`px-3 py-2 min-h-[40px] text-xs font-semibold transition-colors ${magFilter === m.code ? 'bg-red-600 text-white' : 'text-gray-500 hover:bg-white/5'}`}>{m.code}</button>
              ))}
            </div>
            <div className="flex rounded-xl border border-white/10 overflow-hidden bg-black/20">
              {STATUT_FILTERS.map((s) => (
                <button key={s.key} type="button" onClick={() => setStatutFilter(s.key)}
                  className={`px-3 py-2 min-h-[40px] text-xs font-semibold transition-colors ${statutFilter === s.key ? 'bg-red-600 text-white' : 'text-gray-500 hover:bg-white/5'}`}
                  data-testid={`cp-statut-${s.key || 'all'}`}>{t(s.labelKey)}</button>
              ))}
            </div>
            <button type="button" onClick={fetchCommandes} className={STOCK_BTN_ICON} title={t('partsOrders.refresh')}><RefreshCw className="w-4 h-4" /></button>
          </div>
          <button type="button" onClick={() => setShowCreate(true)} className={STOCK_BTN_PRIMARY} data-testid="cp-new-btn">
            <Plus className="w-4 h-4" /> {t('partsOrders.newOrder')}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="text-2xl font-bold text-amber-300">{kpis.enCours}</div>
            <div className="text-xs text-gray-500">{t('partsOrders.kpiOpenOrders')}</div>
          </div>
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="text-2xl font-bold text-sky-300">{kpis.lignesEnCommande}</div>
            <div className="text-xs text-gray-500">{t('partsOrders.kpiAssignedParts')}</div>
          </div>
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="text-2xl font-bold text-white">{formatCurrency(Number(kpis.coutOuvert) || 0)}</div>
            <div className="text-xs text-gray-500">{t('partsOrders.kpiCostToReceive')}</div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('partsOrders.loading')}</div>
        ) : commandes.length === 0 ? (
          <div className={`${STOCK_PANEL} p-10 text-center`}>
            <Package className="w-10 h-10 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">{t('partsOrders.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {commandes.map((c) => (
              <CommandeCard key={c.id} commande={c} api={api} onChanged={applyUpdated} onReceive={setReceiving} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          magasinsList={magasinsList}
          defaultMagasin={magFilter || (magasinsList[0]?.code) || 'M1'}
          api={api}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => { setShowCreate(false); setCommandes((prev) => [created, ...prev]); }}
        />
      )}

      {receiving && (
        <ReceiveModal
          commande={receiving}
          api={api}
          onClose={() => setReceiving(null)}
          onReceived={(updated) => { setReceiving(null); applyUpdated(updated); }}
        />
      )}
    </Layout>
  );
};

export default CommandesPiecesPage;
